/**
 * What the release notes dialog shows. The API bundles every release, newest first,
 * and counts how many of the newest the reader has not seen; the dialog opens on
 * those by itself and on the whole history when the reader asks for it.
 */
import type { ReleaseNewsResponse, ReleaseResponse } from '@contract-analyzer/api-client';

/** `unseen` opens by itself after a release; `all` is the history the reader asked for. */
export type ReleaseNewsView = 'unseen' | 'all';

/** Releases shown in full before the rest fold behind one disclosure. */
const EXPANDED_RELEASES = 2;

const RELEASE_DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  // A changelog date names a calendar day, not an instant.
  timeZone: 'UTC',
});

/** The reader has releases they have not seen and has not closed them this session. */
function hasUnseenReleases(
  news: ReleaseNewsResponse | null,
  acknowledgedVersion: string | null,
): boolean {
  return (
    news !== null &&
    news.available &&
    news.unseen_release_count > 0 &&
    news.version !== acknowledgedVersion
  );
}

/**
 * The version closing the dialog acknowledges: the running release, which covers every
 * earlier one, while unseen releases wait; `null` once there is nothing left to mark seen.
 */
export function versionToAcknowledge(
  news: ReleaseNewsResponse | null,
  acknowledgedVersion: string | null,
): string | null {
  if (news === null || !hasUnseenReleases(news, acknowledgedVersion)) return null;
  return news.version ?? null;
}

/** The open view: the one the reader asked for, else the unseen releases while there are some. */
export function releaseNewsView(
  news: ReleaseNewsResponse | null,
  acknowledgedVersion: string | null,
  requested: ReleaseNewsView | null,
): ReleaseNewsView | null {
  if (requested !== null) return requested;
  return hasUnseenReleases(news, acknowledgedVersion) ? 'unseen' : null;
}

/**
 * Release news owns a page load's one modal slot while it loads, while it is open, and
 * for the rest of a page load that found unseen releases, so another prompt neither
 * stacks on the dialog nor follows straight after it.
 */
export function releaseNewsClaimsPageLoad(
  news: ReleaseNewsResponse | null,
  settled: boolean,
  view: ReleaseNewsView | null,
): boolean {
  if (!settled || view !== null) return true;
  return news !== null && news.available && news.unseen_release_count > 0;
}

export function releasesInView(
  news: ReleaseNewsResponse,
  view: ReleaseNewsView,
): readonly ReleaseResponse[] {
  return view === 'unseen' ? news.releases.slice(0, news.unseen_release_count) : news.releases;
}

/** The newest releases in full; the rest fold so a long gap stays one screen. */
export function foldReleases(releases: readonly ReleaseResponse[]) {
  return {
    expanded: releases.slice(0, EXPANDED_RELEASES),
    earlier: releases.slice(EXPANDED_RELEASES),
  };
}

export function formatReleaseDate(releasedOn: string): string {
  return RELEASE_DATE.format(new Date(`${releasedOn}T00:00:00Z`));
}

/** One line under the title: which release this is, or how many the reader missed. */
export function describeReleases(view: ReleaseNewsView, releases: readonly ReleaseResponse[]): string {
  const [newest] = releases;
  if (newest === undefined) return '';
  const dated = `${newest.version}, ${formatReleaseDate(newest.released_on)}`;
  if (view === 'all') return `${String(releases.length)} releases, newest first`;
  if (releases.length === 1) return `Release ${dated}`;
  return `${String(releases.length)} releases since you last looked, the latest ${dated}`;
}
